import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield, AlertTriangle, ChevronLeft, ChevronRight, ChevronDown, Info, Camera, Users, Building2, ClipboardList, Trash2, Clock } from 'lucide-react';
import { useFilterParams } from "@/store/filtersStore";
import { useUserStore } from '@/store/userStore';
import {
  fetchOPS, fetchOPSResumen, fetchOPSPorTipo, fetchOPSIncumplimientoPorSede, fetchOPSTopPersonas, fetchOPSEspaciosConfinados,
  fetchFilterSedes, fetchFilterNegocios, fetchFilterTipoops,
} from '@/services/api';
import { KpiGlowCard } from '@/components/dashboard/KpiGlowCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { HorizontalBarChart, VerticalBarChart } from '@/components/dashboard/Charts';
import { GlobalFilterBar } from '@/components/dashboard/GlobalFilterBar';
import { CadaPasoPanel } from '@/components/dashboard/CadaPasoPanel';
import { ModuleFilterBar } from '@/components/common/ModuleFilterBar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import ExportButtons from '@/components/common/ExportButtons';
import { filterSinSede, sinSedeDisclaimer } from '@/utils/sedeUtils';
import { PhotoViewerButton } from '@/components/common/PhotoViewer';
import { TrazabilidadFooter } from '@/components/common/TrazabilidadFooter';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const _DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const getDia = (fecha) => { try { return _DIAS[new Date(fecha + 'T12:00:00').getDay()]; } catch { return '-'; } };

// ─── Column filter definitions for OPS ───────────────────────────────────────
const OPS_FILTER_DEFS = [
  { key: 'desde',         label: 'Desde',            type: 'date' },
  { key: 'hasta',         label: 'Hasta',            type: 'date' },
  { key: 'persona',       label: 'Persona',          type: 'text' },
  { key: 'sede',          label: 'Sede',             type: 'select', fetchFn: fetchFilterSedes },
  { key: 'negocio',       label: 'Negocio',          type: 'select', fetchFn: fetchFilterNegocios },
  { key: 'tipoops',       label: 'Tipo OPS',         type: 'select', fetchFn: fetchFilterTipoops },
  { key: 'incumplimiento',label: 'Incumplimiento',   type: 'select', options: ['SI', 'NO'] },
  { key: 'foto',          label: 'Foto',             type: 'select', options: ['Con foto', 'Sin foto'] },
  { key: 'planinc',       label: 'Plan Jornada',     type: 'select', options: ['Con checklist', 'Sin checklist'] },
];

const FIELD_LABELS_OPS = {
  fecha: 'Fecha', persona: 'Persona', sede_label: 'Sede', negocio: 'Negocio',
  tipoops: 'Tipo OPS', incumplimiento: 'Incumplimiento',
  nota: 'Nota', comentariosadicionales: 'Comentarios adicionales',
  otroscomentarios: 'Otros comentarios',
  fecharegistro: 'Fecha de registro',
  fecha_ops: 'Fecha real OPS',
  hora_inicio: 'Hora inicio', hora_fin: 'Hora fin',
  numtrabajadores: 'N.º trabajadores',
  Trabajadores_1: 'Trabajador 1', Trabajadores_2: 'Trabajador 2',
  Trabajadores_3: 'Trabajador 3', Trabajadores_4: 'Trabajador 4',
  mas4trabajadores: 'Más de 4 trabajadores (nota)',
  planinc_observaciones: 'Observaciones Plan Jornada',
  planveh_comentario: 'Comentario Plan Vehículo',
  planinst_comentario: 'Comentario Plan Instalaciones',
};

// Checklist fields: shown in dedicated panels, not in the generic grid
const _PLANINC_KEYS = Array.from({length: 15}, (_, i) => `planinc_${String(i+1).padStart(2,'0')}`);
const _PLANVEH_KEYS = [
  'planveh_sube_baja_frente','planveh_tres_puntos','planveh_apoya_inestable',
  'planveh_revisa_suelo','planveh_salta_cabina','planveh_calzado_v',
  'planveh_veh_inmov','planveh_accesos_ok',
];
const _PLANINST_KEYS = [
  'planinst_senal_barand','planinst_usa_pasaman','planinst_pie_escalon',
  'planinst_sin_distracc','planinst_calzado_i','planinst_escal_visib','planinst_escalera_limp',
];
const _METODO_KEYS = ['opsfecha','ultmasops','observamos','reconocemos','corregimos','escuchamos','preguntamos','explicamos','obtenemos','resumimos'];

const PLANINC_LABELS = {
  planinc_01:'Baja de cara al vehículo (3 puntos, sin saltar)',
  planinc_02:'Escalera: peldaños limpios y usa pasamanos',
  planinc_03:'Carga pesada: solicita ayuda o medios mecánicos',
  planinc_04:'Manipulación cargas: evita girar cintura',
  planinc_05:'Vigilante durante los desplazamientos',
  planinc_06:'Vehículo: cinturón siempre antes de arrancar',
  planinc_07:'Respeta límites de velocidad',
  planinc_08:'Casco abrochado en estribo',
  planinc_09:'Ambas manos sujetas a asideros en estribo',
  planinc_10:'No permanece en estribo en marcha atrás',
  planinc_11:'Uso correcto de EPIs según la tarea',
  planinc_12:'Manos fuera de zonas de corte/atrapamiento',
  planinc_13:'Se hidrata durante la jornada',
  planinc_14:'Aplica medidas de protección contra el calor',
  planinc_15:'Conoce síntomas de golpe de calor',
};
const PLANVEH_LABELS = {
  planveh_sube_baja_frente:'Sube/baja de frente a la cabina',
  planveh_tres_puntos:'Mantiene 3 puntos de contacto',
  planveh_apoya_inestable:'Se agarra a partes inestables',
  planveh_revisa_suelo:'Revisa el suelo antes de bajar',
  planveh_salta_cabina:'Salta desde la cabina',
  planveh_calzado_v:'Calzado adecuado',
  planveh_veh_inmov:'Sube/baja con vehículo inmovilizado',
  planveh_accesos_ok:'Accesos y apoyos en buen estado',
};
const PLANINST_LABELS = {
  planinst_senal_barand:'Señalización de barandilla',
  planinst_usa_pasaman:'Usa el pasamanos',
  planinst_pie_escalon:'Apoya pie en cada escalón',
  planinst_sin_distracc:'Sube/baja sin distracciones',
  planinst_calzado_i:'Calzado adecuado',
  planinst_escal_visib:'Visibilidad adecuada (ilum. + señal.)',
  planinst_escalera_limp:'Escalera limpia y libre de obstáculos',
};
const METODO_LABELS = {
  opsfecha:'OPS realizada en fecha planificada',
  ultmasops:'Revisó últimas OPS de la zona',
  observamos:'Observó las tareas antes de actuar',
  reconocemos:'Reconoció comportamientos',
  corregimos:'Corrigió comportamientos',
  escuchamos:'Escuchó activamente',
  preguntamos:'Invitó a responder',
  explicamos:'Explicó consecuencias',
  obtenemos:'Obtuvo compromiso de la persona',
  resumimos:'Resumió el resultado de la OPS',
};

const CHECKLIST_SKIP = new Set([
  ..._PLANINC_KEYS,
  ..._PLANVEH_KEYS,
  ..._PLANINST_KEYS,
  ..._METODO_KEYS,
  'planinc_total_si','planinc_total_no','planinc_total_np',
  'planinc_alerta_no','planinc_intro','planinc_sec_mov','planinc_sec_veh','planinc_sec_epi',
  'planveh_comentario','planinst_comentario',
  'Trabajadores_1','Trabajadores_2','Trabajadores_3','Trabajadores_4',
  'seleccion','seleccionfoto','numtrabajadores','mas4trabajadores',
]);

const SKIP_FIELDS = new Set([
  'id','_id','objectid','globalid','_synced_at','_layer','sede_codigo','sede','centro',
]);

const VAL_COLOR = {
  'SI': '#16a34a', 'Sí': '#16a34a', 'si': '#16a34a',
  'NO': '#ef4444', 'No': '#ef4444', 'no': '#ef4444',
  'NP': '#6b7280', 'No aplica': '#6b7280', 'No procede': '#6b7280',
  'Parcialmente': '#f59e0b', 'parcialmente': '#f59e0b',
};
function ValBadge({ val }) {
  if (!val || val === '-') return <span className="text-xs" style={{color:'var(--vz-text-muted)'}}>—</span>;
  const col = VAL_COLOR[val] || '#6b7280';
  return <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold" style={{background:`${col}22`,color:col,border:`1px solid ${col}55`}}>{val}</span>;
}
function ChecklistPanel({ title, keys, labels, row, commentKey }) {
  const items = keys.filter(k => row[k] != null && row[k] !== '');
  if (!items.length) return null;
  return (
    <div className="mt-3 px-4 pb-3">
      <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{color:'var(--vz-text-muted)'}}>{title}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
        {items.map(k => (
          <div key={k} className="flex items-start gap-2">
            <ValBadge val={row[k]} />
            <span className="text-xs leading-relaxed" style={{color:'var(--vz-text-secondary)'}}>{labels[k] || k}</span>
          </div>
        ))}
      </div>
      {commentKey && row[commentKey] && (
        <p className="text-xs mt-1.5 italic" style={{color:'var(--vz-text-muted)'}}>{row[commentKey]}</p>
      )}
    </div>
  );
}

// Detect ArcGIS epoch (ms since Unix epoch) and format as local date
const formatFieldValue = (value) => {
  if (typeof value === 'number' && value > 1_000_000_000_000) {
    return new Date(value).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }
  return String(value).replace(/_/g, ' ');
};

const LONG_TEXT_FIELDS = new Set(['nota', 'comentariosadicionales', 'otroscomentarios', 'rsvsi', 'RSVSI']);

function ExpandedDetail({ row }) {
  // Workers involved
  const workers = [1,2,3,4].map(i => row[`Trabajadores_${i}`]).filter(Boolean);

  const entries = Object.entries(row).filter(
    ([k, v]) => !SKIP_FIELDS.has(k.toLowerCase()) && !CHECKLIST_SKIP.has(k) && v != null && v !== '' && v !== '-'
  );
  const longEntries = entries.filter(([k]) => LONG_TEXT_FIELDS.has(k));
  const shortEntries = entries.filter(([k]) => !LONG_TEXT_FIELDS.has(k));
  return (
    <div style={{ background: 'var(--vz-bg-elevated)' }}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 p-4">
        {shortEntries.map(([key, value]) => (
          <div key={key} className="min-w-0">
            <p className="text-[9px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: 'var(--vz-text-muted)' }}>
              {FIELD_LABELS_OPS[key] || key.replace(/_/g, ' ')}
            </p>
            <p className="text-xs truncate" style={{ color: 'var(--vz-text-secondary)' }} title={formatFieldValue(value)}>
              {formatFieldValue(value)}
            </p>
          </div>
        ))}
      </div>
      {longEntries.length > 0 && (
        <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          {longEntries.map(([key, value]) => (
            <div key={key}>
              <p className="text-[9px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'var(--vz-text-muted)' }}>
                {FIELD_LABELS_OPS[key] || key.replace(/_/g, ' ')}
              </p>
              <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--vz-text-secondary)' }}>
                {String(value)}
              </p>
            </div>
          ))}
        </div>
      )}
      {/* Workers panel */}
      {(workers.length > 0 || row.numtrabajadores) && (
        <div className="px-4 pb-3 border-t" style={{borderColor:'var(--vz-border-subtle)'}}>
          <p className="text-[10px] uppercase tracking-wider font-bold mt-2 mb-1.5" style={{color:'var(--vz-text-muted)'}}>
            Trabajadores involucrados {row.numtrabajadores ? `(${row.numtrabajadores})` : ''}
          </p>
          <div className="flex flex-wrap gap-2">
            {workers.map((w, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded-full" style={{background:'var(--vz-bg-card)',border:'1px solid var(--vz-border)',color:'var(--vz-text-secondary)'}}>{w}</span>
            ))}
            {row.mas4trabajadores && <span className="text-xs italic" style={{color:'var(--vz-text-muted)'}}>{row.mas4trabajadores}</span>}
          </div>
        </div>
      )}
      {/* Plan Inicio Jornada checklist */}
      {_PLANINC_KEYS.some(k => row[k]) && (
        <div className="border-t" style={{borderColor:'var(--vz-border-subtle)'}}>
          <ChecklistPanel
            title="Plan de Inicio de Jornada"
            keys={_PLANINC_KEYS} labels={PLANINC_LABELS} row={row}
            commentKey="planinc_observaciones"
          />
        </div>
      )}
      {/* Caídas desde vehículo checklist */}
      {_PLANVEH_KEYS.some(k => row[k]) && (
        <div className="border-t" style={{borderColor:'var(--vz-border-subtle)'}}>
          <ChecklistPanel
            title="Caídas desde Vehículo"
            keys={_PLANVEH_KEYS} labels={PLANVEH_LABELS} row={row}
            commentKey="planveh_comentario"
          />
        </div>
      )}
      {/* Caídas en instalaciones checklist */}
      {_PLANINST_KEYS.some(k => row[k]) && (
        <div className="border-t" style={{borderColor:'var(--vz-border-subtle)'}}>
          <ChecklistPanel
            title="Caídas en Instalaciones Fijas"
            keys={_PLANINST_KEYS} labels={PLANINST_LABELS} row={row}
            commentKey="planinst_comentario"
          />
        </div>
      )}
      {/* Calidad metodológica de la OPS */}
      {_METODO_KEYS.some(k => row[k]) && (
        <div className="border-t" style={{borderColor:'var(--vz-border-subtle)'}}>
          <ChecklistPanel
            title="Calidad Metodológica de la OPS"
            keys={_METODO_KEYS} labels={METODO_LABELS} row={row}
          />
        </div>
      )}
      {(row.objectid || row.OBJECTID) && (
        <div className="px-4 pb-3 flex items-center gap-2 border-t" style={{borderColor:'var(--vz-border-subtle)'}}>
          <PhotoViewerButton module="ops" objectid={row.objectid || row.OBJECTID} />
        </div>
      )}
      <TrazabilidadFooter row={row} />
    </div>
  );
}

function OPSTable({ filters = {} }) {
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const globalFilters = useFilterParams();
  const combinedFilters = { ...globalFilters, ...filters, page, limit: 50 };
  const { token, user } = useUserStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const queryClient = useQueryClient();

  // Reset to page 1 when filters change
  const filtersKey = JSON.stringify(filters);
  React.useEffect(() => { setPage(1); setExpandedRow(null); }, [filtersKey]);

  const { data: listData, isLoading } = useQuery({
    queryKey: ['ops-list', combinedFilters],
    queryFn: () => fetchOPS(combinedFilters),
  });

  const deleteRecord = async (objectid, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este registro? Esta acción no se puede deshacer.')) return;
    try {
      const tok = localStorage.getItem('valoriza_token');
      await axios.delete(`${API}/api/ops/${objectid}`, { headers: { Authorization: `Bearer ${tok}` } });
      toast.success('Registro eliminado');
      queryClient.invalidateQueries({ queryKey: ['ops-list'] });
    } catch { toast.error('Error al eliminar'); }
  };

  const rows   = listData?.data  || [];
  const total  = listData?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const COLS = ['', 'Día', 'Fecha', 'Persona', 'Sede', 'Negocio', 'Tipo OPS', 'Incumplimiento', ''];

  const toggleRow = (i) => setExpandedRow(prev => prev === i ? null : i);

  return (
    <div data-testid="ops-table-container">
      <div className="flex items-center justify-between px-1 mb-2">
        <span className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{total.toLocaleString('es-ES')} registros</span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft size={14} />
          </Button>
          <span className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>{page} / {totalPages || 1}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
      <ScrollArea className="h-[420px]">
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              {COLS.map((h, idx) => (
                <TableHead key={idx} className="text-[10px] uppercase tracking-wider font-montserrat sticky top-0" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)', width: idx === 0 ? 28 : undefined }}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 7 }).map((_, i) => (
                  <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                    {COLS.map((_, j) => <TableCell key={j}><div className="h-3 rounded animate-pulse" style={{ background: 'var(--vz-bg-elevated)', width: '80%' }} /></TableCell>)}
                  </TableRow>
                ))
              : rows.length === 0
                ? <TableRow><TableCell colSpan={9} className="text-center text-xs py-8" style={{ color: 'var(--vz-text-muted)' }}>Sin registros para este filtro</TableCell></TableRow>
                : rows.map((row, i) => (
                    <React.Fragment key={i}>
                      <TableRow
                        onClick={() => toggleRow(i)}
                        className="hover:bg-[#132860] transition-colors duration-200 cursor-pointer"
                        style={{ borderColor: 'var(--vz-border-subtle)', background: expandedRow === i ? 'rgba(74,158,255,0.06)' : undefined }}
                        data-testid="ops-table-row"
                      >
                        <TableCell className="pl-3 pr-1 w-7">
                          <ChevronDown size={12} className="transition-transform duration-200" style={{ color: 'var(--vz-text-muted)', transform: expandedRow === i ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
                        </TableCell>
                        <TableCell className="text-[10px] font-medium w-8 text-center" style={{ color: 'var(--vz-text-muted)' }}>{getDia(row.fecha)}</TableCell>
                        <TableCell className="text-xs font-mono-custom whitespace-nowrap" style={{ color: 'var(--vz-text-muted)' }}>{row.fecha || '-'}</TableCell>
                        <TableCell className="text-xs font-medium max-w-[130px] truncate" style={{ color: 'var(--vz-text-primary)' }}>{row.persona || '-'}</TableCell>
                        <TableCell className="text-xs max-w-[150px]" style={{ color: 'var(--vz-text-secondary)' }} title={(row.sede_label || (row.sede ? row.sede.replace(/_/g, ' ') : row.centro) || '') + (row.sede_codigo ? ` · ${row.sede_codigo}` : '')}>
                          <div className="leading-tight">
                            <div className="truncate">{row.sede_label || (row.sede ? row.sede.replace(/_/g, ' ') : null) || row.centro || '-'}</div>
                            {row.sede_codigo && <div className="text-[10px] font-mono opacity-50">{row.sede_codigo}</div>}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs max-w-[110px] truncate" style={{ color: 'var(--vz-text-secondary)' }}>{row.negocio ? row.negocio.charAt(0).toUpperCase() + row.negocio.slice(1) : '-'}</TableCell>
                        <TableCell className="text-xs max-w-[140px] truncate" style={{ color: 'var(--vz-text-secondary)' }}>{row.tipoops?.replace(/_/g, ' ') || '-'}</TableCell>
                        <TableCell>
                          <Badge className="text-[10px]" style={{
                            background: row.incumplimiento === 'SI' ? 'rgba(255,82,82,0.15)' : 'rgba(0,200,83,0.12)',
                            color: row.incumplimiento === 'SI' ? '#FF5252' : '#00C853',
                            border: `1px solid ${row.incumplimiento === 'SI' ? 'rgba(255,82,82,0.3)' : 'rgba(0,200,83,0.25)'}`,
                          }}>{row.incumplimiento || 'NO'}</Badge>
                        </TableCell>
                        <TableCell className="pr-3">
                          <div className="flex items-center gap-1.5">
                            {row.has_foto && (
                              <Camera size={12} style={{ color: '#4A9EFF' }} title="Tiene foto adjunta" />
                            )}
                            {(row.objectid || row.OBJECTID) && (
                              <PhotoViewerButton module="ops" objectid={row.objectid || row.OBJECTID} />
                            )}
                            {isAdmin && (
                              <button
                                onClick={(e) => deleteRecord(row.objectid || row.OBJECTID, e)}
                                className="ml-1 opacity-30 hover:opacity-100 transition-opacity"
                                title="Eliminar registro"
                                data-testid="ops-delete-btn"
                              >
                                <Trash2 size={11} className="text-red-400" />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedRow === i && (
                        <TableRow data-testid="ops-expanded-row" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                          <TableCell colSpan={9} className="p-0">
                            <ExpandedDetail row={row} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
            }
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}

export default function OPS() {
  const globalFilters = useFilterParams();
  const { token } = useUserStore();
  const [moduleFilters, setModuleFilters] = useState({});

  const mergedFilters = useMemo(() => ({ ...globalFilters, ...moduleFilters }), [globalFilters, moduleFilters]);

  const handleFilterChange = (key, value) => setModuleFilters(prev => ({ ...prev, [key]: value }));
  const handleReset = () => setModuleFilters({});

  const enabled = !!token;
  const { data: tiposOPS,   isLoading: tiposLoading   } = useQuery({ queryKey: ['ops-tipos-page',  mergedFilters], queryFn: () => fetchOPSPorTipo(mergedFilters), enabled });
  const { data: incumSede,  isLoading: incumLoading   } = useQuery({ queryKey: ['ops-incum-sede',  mergedFilters], queryFn: () => fetchOPSIncumplimientoPorSede(mergedFilters), enabled });
  const { data: topPersonas,isLoading: topLoading     } = useQuery({ queryKey: ['ops-top-personas-page', mergedFilters], queryFn: () => fetchOPSTopPersonas({ ...mergedFilters, limit: 2000 }), enabled });
  const { data: resumen,    isLoading: resumenLoading  } = useQuery({ queryKey: ['ops-resumen', mergedFilters], queryFn: () => fetchOPSResumen(mergedFilters), staleTime: 60000, enabled });
  const { data: espConf,    isLoading: espLoading     } = useQuery({ queryKey: ['ops-esp-confinados', mergedFilters], queryFn: () => fetchOPSEspaciosConfinados(mergedFilters), enabled });

  // Sin sede logic on incumSede chart data
  const { valid: incumSedeFiltered, sinSedeCount } = useMemo(() => filterSinSede(incumSede || [], 'sede', 'total'), [incumSede]);
  const incumDisclaimer = sinSedeDisclaimer(sinSedeCount);

  return (
    <div data-testid="ops-page">
      <GlobalFilterBar />
      <div className="px-4 lg:px-6 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-montserrat font-bold text-lg" style={{ color: 'var(--vz-text-primary)' }}>OPS — Observaciones de Seguridad</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>Datos en tiempo real desde ArcGIS</p>
          </div>
          <ExportButtons tipo="ops" filters={mergedFilters} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="ops-kpi-row">
          <KpiGlowCard
            title="Total OPS"
            value={resumen?.total}
            color="#4A9EFF"
            icon={ClipboardList}
            loading={resumenLoading}
            delta={resumen?.delta_pct}
            sub={resumen?.delta_pct != null ? `vs mismo período ${new Date().getFullYear() - 1}` : 'Registros con filtro activo'}
            tooltip="Recuento de OPS que cumplen los filtros activos (fecha, sede, etc.). No es lo mismo que 'OPS global' del Dashboard, que compara contra un objetivo de Excel (Luis 2026)."
            data-testid="ops-kpi-total"
          />
          <KpiGlowCard
            title="Personas que realizan OPS"
            value={resumen?.personas ?? '—'}
            sub="Personas distintas"
            color="#00C853"
            icon={Users}
            loading={resumenLoading}
          />
          <KpiGlowCard
            title="Centros que realizan OPS"
            value={resumen?.centros ?? '—'}
            sub="Centros de trabajo distintos"
            color="#FFD600"
            icon={Building2}
            loading={resumenLoading}
          />
          <KpiGlowCard
            title="Duración media OPS"
            value={resumen?.duracion_media ?? 0}
            unit="min"
            sub={`Tiempo medio hora inicio → fin (n=${resumen?.duracion_n ?? 0})`}
            color="#FF7043"
            icon={Clock}
            loading={resumenLoading}
            tooltip="Duración media en minutos calculada a partir de hora_inicio y hora_fin."
            data-testid="ops-kpi-duracion"
          />
          <KpiGlowCard
            title="% OPS con deficiencias"
            value={resumen?.deficiencias_pct ?? 0}
            unit="%"
            sub="Incumplimiento = SI"
            color="#FF5252"
            icon={AlertTriangle}
            loading={resumenLoading}
            tooltip="Porcentaje de OPS donde se detectó un incumplimiento de normas o comportamiento inseguro."
            data-testid="ops-kpi-deficiencias"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ChartCard title="Tipos de riesgo observado" loading={tiposLoading} isEmpty={!tiposOPS?.length}>
            <HorizontalBarChart data={(tiposOPS || []).map((t) => ({ name: t.tipo, total: t.total }))} nameKey="name" dataKey="total" color="#4A9EFF" height={280} />
          </ChartCard>

          <ChartCard title="Top 10 personas OPS" loading={topLoading} isEmpty={!topPersonas?.length}>
            <HorizontalBarChart data={(topPersonas || []).map((p) => ({ name: p.persona, total: p.total }))} nameKey="name" dataKey="total" color="#00C853" height={280} />
          </ChartCard>
        </div>

        <ChartCard title="Incumplimiento RSV por sede" subtitle="Total vs incumplimientos detectados" loading={incumLoading} isEmpty={!incumSedeFiltered.length}>
          <VerticalBarChart
            data={incumSedeFiltered.slice(0, 12).map((s) => ({ name: s.sede?.length > 15 ? s.sede.substring(0, 13) + '…' : s.sede, total: s.total, incumplimiento: s.incumplimiento }))}
            nameKey="name" dataKey="total" incumplimientoKey="incumplimiento" color="#4A9EFF" height={280}
          />
          {incumDisclaimer && (
            <div className="flex items-start gap-1.5 mt-2 px-1">
              <Info size={11} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--vz-text-muted)' }} />
              <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>{incumDisclaimer}</p>
            </div>
          )}
        </ChartCard>

        {/* Espacios Confinados RSV breakdown — Survey123 spec 2026-04-28 */}
        <ChartCard
          title="Espacios Confinados — Sub-incumplimientos RSV"
          subtitle="Detalle de incumplimientos específicos en trabajos en espacios confinados"
          loading={espLoading}
          isEmpty={!espConf?.items?.length || espConf.total_records === 0}
          emptyMessage={espConf && espConf.total_records === 0 ? "Sin registros con incumplimientos en espacios confinados en el rango seleccionado." : undefined}
        >
          <div className="space-y-1.5" data-testid="ops-espacios-confinados-breakdown">
            {(espConf?.items || []).map((item) => {
              const pct = espConf.total_records > 0 ? Math.round((item.count / espConf.total_records) * 100) : 0;
              return (
                <div key={item.key} className="flex items-center gap-3 py-1">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate" style={{ color: 'var(--vz-text-primary)' }}>{item.label}</p>
                    <div className="h-1.5 mt-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: item.count > 0 ? '#F97316' : 'transparent' }} />
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <span className="text-xs font-mono font-semibold" style={{ color: item.count > 0 ? '#F97316' : 'var(--vz-text-muted)' }}>{item.count}</span>
                    <span className="text-[10px] ml-1.5" style={{ color: 'var(--vz-text-muted)' }}>({pct}%)</span>
                  </div>
                </div>
              );
            })}
            {espConf?.total_records > 0 && (
              <p className="text-[10px] mt-2 pt-2 border-t" style={{ color: 'var(--vz-text-muted)', borderColor: 'var(--vz-border-subtle)' }}>
                Sobre {espConf.total_records} OPS con observaciones en espacios confinados.
              </p>
            )}
          </div>
        </ChartCard>

        {/* Cada paso cuenta — módulo observación tropiezos (Survey123 2026-10) */}
        <CadaPasoPanel filters={mergedFilters} enabled={enabled} />

        {/* Table section with module-specific filters */}
        <div className="rounded-2xl border p-4 space-y-4" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
          <h3 className="font-montserrat text-xs font-bold uppercase tracking-[0.16em]" style={{ color: 'var(--vz-text-secondary)' }}>
            Registros OPS
          </h3>
          <ModuleFilterBar filters={moduleFilters} onChange={handleFilterChange} onReset={handleReset} filterDefs={OPS_FILTER_DEFS} />
          <OPSTable filters={moduleFilters} />
        </div>
      </div>
    </div>
  );
}
